import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { Button, SearchField } from "@legends/ui";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import DataGrid from "./DataGrid";
import SearchableSelect from "./admin/SearchableSelect";
import { AsyncState, errMsg, monoLabelStyle } from "./sections";
import {
  fetchMarketingCustomer,
  fetchMarketingCustomers,
  fetchMarketingHealth,
  fetchMarketingRecommendations,
  runMarketingBackfill,
  type BackfillResult,
  type IntegrationHealth,
  type MarketingCustomerDetail,
  type MarketingCustomerRow,
  type MarketingHealth,
  type MarketingRecommendation,
} from "./api";

// Marketing / CRM / recommendation admin (ADR 0198). A super-admin console for the
// CRM/marketing/recommendation POC: browse customers with their CRM/marketing summary,
// debug per-user recommendations, and check integration connectivity + run backfills.
// Three sub-tabs mirror the cave object page's tablist (CaveDetailScreen), the grids are
// the shared Cave-themed AG Grid (ADR 0156), and everything styles via CSS vars under
// the admin token layer (HomeScreen sets `admin-scope` on <body>).

// Admin-local money format with thousands separators (mirrors MetricsScreen). Cents → "$1,234.00".
const formatMoney = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MARKETING_TABS = ["customers", "recommendations", "health"] as const;
type MarketingTab = (typeof MARKETING_TABS)[number];
const TAB_LABEL: Record<MarketingTab, string> = {
  customers: "Customers",
  recommendations: "Recommendations",
  health: "Integration health",
};

const PAGE_SIZE = 25;

// ── shared styles ────────────────────────────────────────────────────────────

const cardStyle: CSSProperties = {
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-lg)",
  padding: 16,
};

const chipStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "3px 10px",
  borderRadius: "var(--radius-pill)",
  background: "var(--surface-3)",
  border: "1px solid var(--border)",
  fontSize: "var(--fs-2xs)",
  fontFamily: "var(--font-mono)",
  color: "var(--text-2)",
  whiteSpace: "nowrap",
};

function linkButtonStyle(): CSSProperties {
  return {
    background: "transparent",
    border: "1px solid var(--border-strong, var(--border))",
    borderRadius: "var(--radius-md)",
    color: "var(--accent, #c9a227)",
    cursor: "pointer",
    font: "inherit",
    fontSize: "var(--fs-2xs)",
    fontFamily: "var(--font-mono)",
    padding: "3px 10px",
    textTransform: "uppercase",
    letterSpacing: "var(--ls-label)",
  };
}

export default function MarketingScreen() {
  const [tab, setTab] = useState<MarketingTab>("customers");

  return (
    <div>
      <h1 style={{ margin: 0, ...monoLabelStyle, fontWeight: 600 }}>Legends · Marketing</h1>
      <p style={{ margin: "10px 0 0", fontSize: "var(--fs-body)", color: "var(--text-dim)" }}>
        CRM, recommendations and lifecycle marketing for the platform's customers.
      </p>

      {/* sub-tabs — mirrors CaveDetailScreen's tablist */}
      <div
        role="tablist"
        aria-label="Marketing sections"
        style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border)", margin: "18px 0 20px", overflowX: "auto" }}
      >
        {MARKETING_TABS.map((t) => {
          const active = t === tab;
          return (
            <button
              key={t}
              role="tab"
              aria-selected={active}
              type="button"
              onClick={() => setTab(t)}
              style={{
                background: "none",
                border: "none",
                borderBottom: `2px solid ${active ? "var(--gold-500, var(--accent))" : "transparent"}`,
                padding: "0 12px",
                height: "var(--h-tab, 40px)",
                color: active ? "var(--text-1)" : "var(--text-3)",
                fontSize: "var(--fs-md)",
                fontWeight: active ? 600 : 400,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {TAB_LABEL[t]}
            </button>
          );
        })}
      </div>

      {tab === "customers" && <CustomersTab />}
      {tab === "recommendations" && <RecommendationsTab />}
      {tab === "health" && <HealthTab />}
    </div>
  );
}

// ── Customers ─────────────────────────────────────────────────────────────────

function CustomersTab() {
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<{
    customers: MarketingCustomerRow[];
    total: number;
    limit: number;
    offset: number;
  } | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);

  // Debounce the search box → q; reset to the first page when the query changes.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(qInput.trim());
      setOffset(0);
    }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const load = useCallback(() => {
    let cancelled = false;
    setStatus("loading");
    setErr(null);
    fetchMarketingCustomers({ limit: PAGE_SIZE, offset, q: q || undefined })
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setStatus("ready");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setErr(errMsg(e));
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [offset, q]);

  useEffect(() => load(), [load]);

  const columns = useMemo<ColDef<MarketingCustomerRow>[]>(
    () => [
      { field: "userId", headerName: "User", type: "numericColumn", maxWidth: 90 },
      { field: "name", headerName: "Name", minWidth: 150, flex: 2, valueGetter: (p) => p.data?.name || "—" },
      { field: "email", headerName: "Email", minWidth: 190, flex: 2 },
      { field: "accountStatus", headerName: "Status", minWidth: 110 },
      { field: "ownedCaves", headerName: "Caves", type: "numericColumn", maxWidth: 100 },
      {
        colId: "ownedSports",
        headerName: "Sports",
        minWidth: 150,
        flex: 2,
        valueGetter: (p) => (p.data?.ownedSports ?? []).join(", "),
      },
      {
        field: "lifetimeValueCents",
        headerName: "Lifetime value",
        type: "numericColumn",
        minWidth: 130,
        valueFormatter: (p) => formatMoney(Number(p.value ?? 0)),
      },
      {
        colId: "view",
        headerName: "",
        pinned: "right",
        width: 78,
        minWidth: 78,
        maxWidth: 78,
        sortable: false,
        filter: false,
        editable: false,
        resizable: false,
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
        cellRenderer: (p: ICellRendererParams<MarketingCustomerRow>) =>
          p.data ? (
            <button type="button" style={linkButtonStyle()} onClick={() => setSelected(p.data!.userId)}>
              View
            </button>
          ) : null,
      },
    ],
    [],
  );

  const total = data?.total ?? 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);

  return (
    <div>
      <section aria-label="Customers">
        <div style={{ margin: "0 0 12px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <h2 style={{ ...monoLabelStyle, margin: 0 }}>Customers</h2>
          <div style={{ marginLeft: "auto" }}>
            <SearchField
              value={qInput}
              onChange={setQInput}
              placeholder="Search name or email…"
              aria-label="Search customers"
              width={280}
            />
          </div>
        </div>

        {status === "error" ? (
          <AsyncState status="error" errorText={err ?? "Couldn't load customers."} onRetry={load} />
        ) : status === "loading" && !data ? (
          <AsyncState status="loading" loadingText="Loading customers…" />
        ) : (
          <>
            <DataGrid<MarketingCustomerRow>
              rows={data?.customers ?? []}
              columns={columns}
              getRowId={(r) => String(r.userId)}
              label="Customers"
              emptyText={q ? `No customers match “${q}”.` : "No customers yet."}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginTop: 10,
                fontSize: "var(--fs-2xs)",
                color: "var(--text-dim)",
                fontFamily: "var(--font-mono)",
              }}
            >
              <span>
                {from}–{to} of {total}
              </span>
              <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                <Button size="sm" variant="muted" disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}>
                  ‹ Prev
                </Button>
                <Button size="sm" variant="muted" disabled={to >= total} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
                  Next ›
                </Button>
              </div>
            </div>
          </>
        )}
      </section>

      {selected != null && (
        <CustomerDetail userId={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function CustomerDetail({ userId, onClose }: { userId: number; onClose: () => void }) {
  const [detail, setDetail] = useState<MarketingCustomerDetail | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    setStatus("loading");
    setErr(null);
    fetchMarketingCustomer(userId)
      .then((d) => {
        setDetail(d);
        setStatus("ready");
      })
      .catch((e: unknown) => {
        setErr(errMsg(e));
        setStatus("error");
      });
  }, [userId]);

  useEffect(() => load(), [load]);

  const labelStyle: CSSProperties = { ...monoLabelStyle, fontSize: "var(--fs-3xs)", marginBottom: 6 };
  const valueStyle: CSSProperties = { fontSize: "var(--fs-body-2)", color: "var(--text-1)" };

  return (
    <section
      aria-label="Customer detail"
      style={{ ...cardStyle, marginTop: 18 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <h2 style={{ ...monoLabelStyle, margin: 0 }}>
          {detail ? `${detail.name || "Customer"} · #${detail.userId}` : `Customer #${userId}`}
        </h2>
        <button type="button" style={{ ...linkButtonStyle(), marginLeft: "auto" }} onClick={onClose}>
          Close
        </button>
      </div>

      {status === "error" ? (
        <AsyncState status="error" errorText={err ?? "Couldn't load this customer."} onRetry={load} />
      ) : status === "loading" || !detail ? (
        <AsyncState status="loading" loadingText="Loading customer…" />
      ) : (
        <div style={{ display: "grid", gap: 18 }}>
          {/* summary row */}
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div>
              <div style={labelStyle}>Email</div>
              <div style={valueStyle}>{detail.email || "—"}</div>
            </div>
            <div>
              <div style={labelStyle}>Account</div>
              <div style={valueStyle}>{detail.accountStatus || "—"}</div>
            </div>
            <div>
              <div style={labelStyle}>Lifetime value</div>
              <div style={valueStyle}>{formatMoney(detail.lifetimeValueCents)}</div>
            </div>
            <div>
              <div style={labelStyle}>Marketing consent</div>
              <div style={valueStyle}>{detail.marketingConsent ? "Opted in" : "Not opted in"}</div>
            </div>
            <div>
              <div style={labelStyle}>CRM</div>
              <div style={valueStyle}>
                {detail.crm?.twentyUrl ? (
                  <a href={detail.crm.twentyUrl} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent, #c9a227)" }}>
                    Open in Twenty ↗
                  </a>
                ) : (
                  <span style={{ color: "var(--text-dim)" }}>Not synced</span>
                )}
              </div>
            </div>
          </div>

          {/* owned sports */}
          <div>
            <div style={labelStyle}>Owned sports</div>
            {detail.ownedSports.length ? (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {detail.ownedSports.map((s) => (
                  <span key={s} style={chipStyle}>
                    {s}
                  </span>
                ))}
              </div>
            ) : (
              <div style={{ ...valueStyle, color: "var(--text-dim)" }}>None</div>
            )}
          </div>

          {/* caves owned */}
          <div>
            <div style={labelStyle}>Caves owned</div>
            {detail.caves.length ? (
              <div style={{ display: "grid", gap: 8 }}>
                {detail.caves.map((c) => (
                  <div
                    key={c.caveId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      flexWrap: "wrap",
                      padding: "8px 12px",
                      background: "var(--surface-3)",
                      border: "1px solid var(--border)",
                      borderRadius: "var(--radius-md)",
                    }}
                  >
                    <span style={{ ...valueStyle, fontWeight: 600 }}>{c.name || c.caveId}</span>
                    {c.legendName && <span style={{ color: "var(--text-2)", fontSize: "var(--fs-2xs)" }}>{c.legendName}</span>}
                    {c.sport && <span style={chipStyle}>{c.sport}</span>}
                    <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                      <span style={chipStyle}>{c.ticketKind}</span>
                      <span style={chipStyle}>{c.status}</span>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ ...valueStyle, color: "var(--text-dim)" }}>None</div>
            )}
          </div>

          {/* recommendations */}
          <div>
            <div style={labelStyle}>Recommendations</div>
            <RecommendationList recs={detail.recommendations} />
          </div>

          {/* segment tags */}
          <div>
            <div style={labelStyle}>Segment tags</div>
            {detail.segmentTags.length ? (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {detail.segmentTags.map((t) => (
                  <span key={t} style={chipStyle}>
                    {t}
                  </span>
                ))}
              </div>
            ) : (
              <div style={{ ...valueStyle, color: "var(--text-dim)" }}>None</div>
            )}
          </div>

          {/* per-integration enabled */}
          <div>
            <div style={labelStyle}>Integrations</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {Object.entries(detail.integrations).map(([name, on]) => (
                <span
                  key={name}
                  style={{
                    ...chipStyle,
                    color: on ? "var(--ok)" : "var(--text-dim)",
                    borderColor: on ? "var(--ok)" : "var(--border)",
                  }}
                >
                  <span aria-hidden style={{ width: 7, height: 7, borderRadius: "50%", background: on ? "var(--ok)" : "var(--text-dim)" }} />
                  {integrationLabel(name)}
                </span>
              ))}
              {Object.keys(detail.integrations).length === 0 && (
                <span style={{ ...valueStyle, color: "var(--text-dim)" }}>None configured</span>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ── Recommendations ─────────────────────────────────────────────────────────

function RecommendationsTab() {
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [optStatus, setOptStatus] = useState<"loading" | "ready" | "error">("loading");
  const [userId, setUserId] = useState("");
  const [recs, setRecs] = useState<MarketingRecommendation[] | null>(null);
  const [gorseEnabled, setGorseEnabled] = useState(true);
  const [recStatus, setRecStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [recErr, setRecErr] = useState<string | null>(null);

  // The customer directory drives the picker. A generous page (POC scale) is enough;
  // SearchableSelect filters it client-side as the operator types.
  const loadOptions = useCallback(() => {
    setOptStatus("loading");
    fetchMarketingCustomers({ limit: 200 })
      .then((res) => {
        setOptions(
          res.customers.map((c) => ({
            value: String(c.userId),
            label: `${c.name || `#${c.userId}`}${c.email ? ` · ${c.email}` : ""}`,
          })),
        );
        setOptStatus("ready");
      })
      .catch(() => setOptStatus("error"));
  }, []);
  useEffect(() => loadOptions(), [loadOptions]);

  const loadRecs = useCallback((id: string) => {
    if (!id) return;
    setRecStatus("loading");
    setRecErr(null);
    fetchMarketingRecommendations(Number(id))
      .then((res) => {
        setRecs(res.recommendations ?? []);
        setGorseEnabled(res.gorseEnabled);
        if (res.error) setRecErr(res.error);
        setRecStatus("ready");
      })
      .catch((e: unknown) => {
        setRecErr(errMsg(e));
        setRecStatus("error");
      });
  }, []);

  const onPick = (id: string) => {
    setUserId(id);
    loadRecs(id);
  };

  return (
    <section aria-label="Recommendations">
      <h2 style={{ ...monoLabelStyle, margin: "0 0 8px" }}>Recommendations</h2>
      <p style={{ margin: "0 0 14px", fontSize: "var(--fs-2xs)", color: "var(--text-dim)" }}>
        Debug the recommended caves for a customer — the same list the fan sees on their home tab.
      </p>

      <div style={{ maxWidth: 420, marginBottom: 18 }}>
        {optStatus === "error" ? (
          <AsyncState status="error" errorText="Couldn't load the customer directory." onRetry={loadOptions} />
        ) : (
          <SearchableSelect
            options={options}
            value={userId}
            onChange={onPick}
            ariaLabel="Customer"
            placeholder={optStatus === "loading" ? "Loading customers…" : "Search a customer…"}
            disabled={optStatus !== "ready"}
          />
        )}
      </div>

      {!gorseEnabled && recStatus === "ready" && (
        <p role="status" style={{ margin: "0 0 12px", fontSize: "var(--fs-2xs)", color: "var(--warn)" }}>
          Gorse recommender is disabled — showing fallback recommendations (if any).
        </p>
      )}
      {recErr && (
        <p role="alert" style={{ margin: "0 0 12px", fontSize: "var(--fs-2xs)", color: "var(--live)" }}>
          {recErr}
        </p>
      )}

      {recStatus === "loading" ? (
        <AsyncState status="loading" loadingText="Loading recommendations…" />
      ) : recStatus === "ready" && recs ? (
        <RecommendationList recs={recs} />
      ) : recStatus === "idle" ? (
        <p style={{ margin: 0, fontSize: "var(--fs-body-2)", color: "var(--text-dim)" }}>
          Pick a customer to see their recommendations.
        </p>
      ) : null}
    </section>
  );
}

/** A recommended-cave list with a normalized score bar + numeric score. Shared by the
 *  customer detail and the Recommendations tab. */
function RecommendationList({ recs }: { recs: MarketingRecommendation[] }) {
  const max = useMemo(() => Math.max(1e-9, ...recs.map((r) => r.score || 0)), [recs]);
  if (!recs.length) {
    return <p style={{ margin: 0, fontSize: "var(--fs-body-2)", color: "var(--text-dim)" }}>No recommendations.</p>;
  }
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {recs.map((r, i) => {
        const pct = Math.max(4, Math.round(((r.score || 0) / max) * 100));
        return (
          <div
            key={`${r.caveId}-${i}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "8px 12px",
              background: "var(--surface-3)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-md)",
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: "var(--fs-body-2)", color: "var(--text-1)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.name || r.caveId}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 2, fontSize: "var(--fs-2xs)", color: "var(--text-2)" }}>
                {r.legendName && <span>{r.legendName}</span>}
                {r.sport && <span style={{ color: "var(--text-dim)" }}>{r.sport}</span>}
              </div>
            </div>
            <div style={{ width: 120, display: "flex", alignItems: "center", gap: 8 }}>
              <div
                aria-hidden
                style={{ flex: 1, height: 6, borderRadius: 3, background: "var(--surface-1)", overflow: "hidden" }}
              >
                <div style={{ width: `${pct}%`, height: "100%", background: "var(--accent, #c9a227)" }} />
              </div>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--fs-2xs)", color: "var(--text-2)", fontVariantNumeric: "tabular-nums" }}>
                {(r.score ?? 0).toFixed(3)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Integration health ────────────────────────────────────────────────────────

const INTEGRATION_LABELS: Record<string, string> = {
  twenty: "Twenty CRM",
  gorse: "Gorse",
  mautic: "Mautic",
};
function integrationLabel(key: string): string {
  return INTEGRATION_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

type HealthTone = { label: string; color: string };
function healthTone(h: IntegrationHealth): HealthTone {
  if (!h.enabled) return { label: "Disabled", color: "var(--text-dim)" };
  if (h.ok) return { label: "Connected", color: "var(--ok)" };
  return { label: "Unavailable", color: "var(--live)" };
}

function HealthTab() {
  const [health, setHealth] = useState<MarketingHealth | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    setStatus("loading");
    setErr(null);
    fetchMarketingHealth()
      .then((h) => {
        setHealth(h);
        setStatus("ready");
      })
      .catch((e: unknown) => {
        setErr(errMsg(e));
        setStatus("error");
      });
  }, []);
  useEffect(() => load(), [load]);

  if (status === "error") {
    return <AsyncState status="error" errorText={err ?? "Couldn't load integration health."} onRetry={load} />;
  }
  if (status === "loading" || !health) {
    return <AsyncState status="loading" loadingText="Checking integrations…" />;
  }

  const order: ("twenty" | "gorse" | "mautic")[] = ["twenty", "gorse", "mautic"];
  const counts = health.counts ?? {};
  const countEntries: [string, number | undefined][] = [
    ["Customers", counts.customers],
    ["Legends", counts.legends],
    ["Caves", counts.caves],
  ];

  return (
    <div style={{ display: "grid", gap: 22 }}>
      <section aria-label="Integrations">
        <h2 style={{ ...monoLabelStyle, margin: "0 0 12px" }}>Integrations</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14 }}>
          {order.map((key) => {
            const h = health.integrations[key];
            const tone = h ? healthTone(h) : { label: "Unknown", color: "var(--text-dim)" };
            return (
              <div key={key} role="group" aria-label={integrationLabel(key)} style={cardStyle}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span aria-hidden style={{ width: 9, height: 9, borderRadius: "50%", background: tone.color }} />
                  <span style={{ fontFamily: "var(--font-display)", fontSize: "var(--fs-lg)", fontWeight: 600, color: "var(--text-1)" }}>
                    {integrationLabel(key)}
                  </span>
                  <span style={{ marginLeft: "auto", ...monoLabelStyle, color: tone.color }}>{tone.label}</span>
                </div>
                <p style={{ margin: "10px 0 0", fontSize: "var(--fs-2xs)", color: "var(--text-dim)", lineHeight: 1.5 }}>
                  {h?.detail || (h?.enabled ? "" : "Not configured.")}
                </p>
              </div>
            );
          })}
        </div>

        {/* record counts */}
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginTop: 14, fontSize: "var(--fs-2xs)", color: "var(--text-dim)", fontFamily: "var(--font-mono)" }}>
          {countEntries.map(([label, n]) => (
            <span key={label}>
              {label}: <span style={{ color: "var(--text-1)" }}>{n ?? "—"}</span>
            </span>
          ))}
        </div>
      </section>

      <BackfillPanel />
    </div>
  );
}

function BackfillPanel() {
  const [dryRun, setDryRun] = useState(true);
  const [running, setRunning] = useState<null | "crm" | "recommendations" | "marketing">(null);
  const [result, setResult] = useState<BackfillResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const run = async (domain: "crm" | "recommendations" | "marketing") => {
    setRunning(domain);
    setErr(null);
    setResult(null);
    try {
      const res = await runMarketingBackfill(domain, { dryRun });
      setResult(res);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setRunning(null);
    }
  };

  const domains: { id: "crm" | "recommendations" | "marketing"; label: string }[] = [
    { id: "crm", label: "CRM" },
    { id: "recommendations", label: "Recommendations" },
    { id: "marketing", label: "Marketing" },
  ];

  return (
    <section aria-label="Backfill" style={cardStyle}>
      <h2 style={{ ...monoLabelStyle, margin: "0 0 6px" }}>Backfill</h2>
      <p style={{ margin: "0 0 14px", fontSize: "var(--fs-2xs)", color: "var(--text-dim)", lineHeight: 1.5 }}>
        Re-sync existing customers, recommendations or marketing contacts to their integrations. Run a dry
        run first to preview the volume without writing.
      </p>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        {domains.map((d) => (
          <Button
            key={d.id}
            size="sm"
            variant="accent"
            disabled={running !== null}
            onClick={() => void run(d.id)}
            aria-label={`Backfill ${d.label}`}
          >
            {running === d.id ? "Running…" : `Backfill ${d.label}`}
          </Button>
        ))}
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: 6, fontSize: "var(--fs-2xs)", color: "var(--text-dim)", cursor: "pointer" }}>
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} aria-label="Dry run" disabled={running !== null} />
          Dry run
        </label>
      </div>

      {err && (
        <p role="alert" style={{ margin: "12px 0 0", fontSize: "var(--fs-2xs)", color: "var(--live)" }}>
          {err}
        </p>
      )}

      {result && (
        <div role="status" aria-label="Backfill result" style={{ marginTop: 14, background: "var(--surface-3)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", padding: 14 }}>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: "var(--fs-body-2)", color: "var(--text-1)" }}>
            <span>
              <strong style={{ color: "var(--text-1)" }}>{result.domain}</strong>{" "}
              {result.dryRun && <span style={{ color: "var(--warn)", fontSize: "var(--fs-2xs)", fontFamily: "var(--font-mono)" }}>DRY RUN</span>}
            </span>
            <span style={{ color: "var(--text-2)" }}>Processed: <span style={{ fontFamily: "var(--font-mono)" }}>{result.processed}</span></span>
            <span style={{ color: "var(--ok)" }}>Succeeded: <span style={{ fontFamily: "var(--font-mono)" }}>{result.succeeded}</span></span>
            <span style={{ color: result.failed ? "var(--live)" : "var(--text-2)" }}>
              Failed: <span style={{ fontFamily: "var(--font-mono)" }}>{result.failed}</span>
            </span>
          </div>
          {result.byEntity && Object.keys(result.byEntity).length > 0 && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
              {Object.entries(result.byEntity).map(([k, v]) => (
                <span key={k} style={chipStyle}>
                  {k}: {v}
                </span>
              ))}
            </div>
          )}
          {result.note && (
            <p style={{ margin: "10px 0 0", fontSize: "var(--fs-2xs)", color: "var(--text-dim)" }}>{result.note}</p>
          )}
        </div>
      )}
    </section>
  );
}
